import { Component, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AuthStoreService, ContentStoreService, NavMenu, Promotion } from '@motivforce/mx-library-angular';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, skip, take } from 'rxjs';
import { PromotionBigBangSideBarData } from 'src/app/core/model/promotion/promotion-big-bang-sidebar-data';
import { TruScaleSidebarData } from 'src/app/core/model/promotion/truscale-sidebar-data';
import { PromotionStoreService } from 'src/app/core/store/promotion-store.service';

import { PromotionUltimateRaceSummaryData } from '../../../model/promotion/promotion-ultimate-race-summary-data';
import { UserStoreService } from '../../../store/user-store.service';

@UntilDestroy()
@Component({
  selector: 'app-profile-menu',
  templateUrl: './profile-menu.component.html',
  styleUrls: ['./profile-menu.component.scss'],
})
export class ProfileMenuComponent implements OnInit {
  user$ = this.authStore.userSettings$.pipe(map((settings) => (settings ? settings.user : null)));

  profileMenu: NavMenu;
  enhancedProfileMenu: NavMenu;

  intelPremiumRewardsSidebarData: any;
  ssgEmeaSidebarData: any;
  valenciaSummaryData: any;
  ultimateRaceSummaryData: PromotionUltimateRaceSummaryData;
  bigBangSummaryData: PromotionBigBangSideBarData;
  truScaleSidebarData: TruScaleSidebarData;

  showRewardCatalogWidgets = false;
  showMastercarWidgets = false;

  intelPremiumRewardsId: number | null = null;
  smarterSellerPromotion: Promotion;
  smarterSellerPromotionId: number | null = null;
  bigBangPromotionId: number | null = null;

  countryCode: string;

  constructor(
    private offcanvasService: NgbOffcanvas,
    private authStore: AuthStoreService,
    private router: Router,
    private contentStore: ContentStoreService,
    private translateService: TranslateService,
    private promotionStore: PromotionStoreService,
    private userStore: UserStoreService
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((event) => event instanceof NavigationStart)
      )
      .subscribe(() => {
        this.offcanvasService.dismiss();
      });

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.showRewardCatalogWidgets =
            event.url.includes('rewards-catalogue') ||
            event.url.includes('points-statement') ||
            event.url.endsWith('take-a-course') ||
            event.url.endsWith('workstation-expert-partner-lounge');
          this.showMastercarWidgets = event.url.includes('my-lenovo-mastercard');
        }
      });

    this.contentStore.headerMenu$.pipe(untilDestroyed(this)).subscribe(() => {
      this.userStore.getProfileMenu(this.translateService.currentLang);
    });

    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe((translation) => {
      if (translation?.lang) {
        this.userStore.getProfileMenu(translation.lang);
      }
    });

    this.promotionStore.currentPromotion$.pipe(skip(1), take(1)).subscribe((promotion: any) => {
      if (promotion.promotionProperties) {
        this.intelPremiumRewardsSidebarData = promotion.promotionProperties.find(
          (property: any) => !!property.intelPremiumRewardsSidebarData
        )?.intelPremiumRewardsSidebarData;

        this.ssgEmeaSidebarData = promotion.promotionProperties.find(
          (property: any) => !!property.ssgEmeaSidebarData
        )?.ssgEmeaSidebarData;

        this.valenciaSummaryData = promotion.promotionProperties.find(
          (property: any) => !!property.valenciaSummaryData
        )?.valenciaSummaryData;

        this.ultimateRaceSummaryData = promotion.promotionProperties.find(
          (property: any) => !!property.ultimateRaceAIPCSummaryData
        )?.ultimateRaceAIPCSummaryData;

        this.bigBangSummaryData = promotion.promotionProperties.find(
          (property: any) => !!property.bigBangSummaryData
        )?.bigBangSummaryData;

        this.truScaleSidebarData = promotion.promotionProperties.find(
          (property: any) => !!property.truScaleSidebarData
        )?.truScaleSidebarData;
      }
    });

    this.userStore.profileMenu$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((profileMenu) => {
      this.profileMenu = profileMenu;

      this.setEnhancedProfileMenu();
    });

    this.promotionStore.promotions$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((promotions) => {
      this.promotionStore.currentSmarterSeller$.pipe(skip(1), take(1)).subscribe((promotion: any) => {
        this.smarterSellerPromotion = promotion;

        const smartSellerPromo = promotion;

        if (smartSellerPromo && !(smartSellerPromo.isApproved === false)) {
          this.smarterSellerPromotionId = smartSellerPromo.id;
        }
      });

      this.promotionStore.getAvailableUserSmartSellerPromotion();
      const intelPremiumRewardsPromo = promotions.find(
        (promotion) => promotion.description && promotion.description?.indexOf('Intel Premium Rewards') !== -1
      );
      if (intelPremiumRewardsPromo) {
        this.intelPremiumRewardsId = intelPremiumRewardsPromo.id;
      }

      const bigBangPromotionPromo = promotions.find(
        (promotion) => promotion.description && promotion.description?.indexOf('Score Big') !== -1
      );
      if (bigBangPromotionPromo) {
        this.bigBangPromotionId = bigBangPromotionPromo.id;
      }

      this.setEnhancedProfileMenu();
    });

    this.authStore.isRegularUser$.pipe(untilDestroyed(this), filter(Boolean)).subscribe(() => {
      this.countryCode = this.authStore.userSettings?.country.alpha2Code;
    });
  }

  open(content: any) {
    this.offcanvasService.open(content, {
      ariaLabelledBy: 'offcanvas-basic-title',
      position: 'end',
      panelClass: 'profile-menu-panel',
      scroll: true,
    });
  }

  logOut() {
    this.authStore.authenticatedUser$.pipe(untilDestroyed(this), skip(1), take(1)).subscribe(() => {
      window.location.reload();
    });

    this.authStore.signOut();
  }

  private setEnhancedProfileMenu(): void {
    this.enhancedProfileMenu = this.profileMenu;

    if (this.smarterSellerPromotionId && this.profileMenu) {
      this.enhancedProfileMenu = {
        ...this.enhancedProfileMenu,
        items: [
          ...this.enhancedProfileMenu.items,
          { url: `/promotions/${this.smarterSellerPromotionId}`, label: 'Smarter Seller Promotion' },
        ],
      };
    }

    if (this.intelPremiumRewardsId && this.profileMenu) {
      this.enhancedProfileMenu = {
        ...this.enhancedProfileMenu,
        items: [
          ...this.enhancedProfileMenu.items,
          { url: `/promotions/${this.intelPremiumRewardsId}`, label: 'Intel Premium Rewards' },
        ],
      };
    }

    if (this.bigBangPromotionId && this.profileMenu) {
      this.enhancedProfileMenu = {
        ...this.enhancedProfileMenu,
        items: [
          ...this.enhancedProfileMenu.items,
          { url: `/promotions/${this.bigBangPromotionId}`, label: 'Score big' },
        ],
      };
    }
  }
}
